import { ApiAction, DefineApiScope, DefineScopedApiAction } from "@root/api/types";

export type LineupPhase =
  | 'upcoming'    // upcoming, but not yet "open for submission"
  | 'open'        // lineup can be set until countdown runs out
  | 'closed'      // countdown over, not yet scored
  | 'scored'      // countdown over and received final scoring (though sometimes the scores can be fixed/changed afterwards)
  | 'cancelled';

export type LineupID = string;
export type LineupDateTime = string;
export type TeamID = string;
export type LineupOptionID = string;
export type LineupOptionsVersion = string;

export const LINEUP_POSITIONS = ['goal', 'for', 'def'] as const;

export type LineupPosition = (typeof LINEUP_POSITIONS)[number];

export const GOAL_SLOT_KEYS = [
  'goal',
  'goalsub',
] as const;

export const DEF_SLOT_KEYS = [
  'def1',
  'def2',
  'def3',
  'def4',
  'def5',
  'def6',
  'def7',
] as const;

export const FOR_SLOT_KEYS = [
  'for1',
  'for2',
  'for3',
  'for4',
  'for5',
  'for6',
  'for7',
  'for8',
  'for9',
  'for10',
  'for11',
  'for12',
] as const;

export const SLOT_KEYS = [
  ...GOAL_SLOT_KEYS,
  ...DEF_SLOT_KEYS,
  ...FOR_SLOT_KEYS
] as const;

export type SlotKey = typeof SLOT_KEYS[number];

export const posForSlot = (slot: SlotKey): LineupPosition => {
  if (FOR_SLOT_KEYS.indexOf(slot as (typeof FOR_SLOT_KEYS)[number]) > -1) { return 'for'; }
  if (DEF_SLOT_KEYS.indexOf(slot as (typeof DEF_SLOT_KEYS)[number]) > -1) { return 'def'; }
  return 'goal';
}

export interface LineupValidity {
  valid: boolean,                   // Is the lineup valid to be used for the matchday?
  invalid_reason?: string,          // Babel sub-key for 'lineup.invalid_reason.*'
  mark_invalid_slots?: SlotKey[],
  is_critical?: boolean,
};

export interface LineupOption {
  id: LineupOptionID,
  price: number,              // how many pucks does this athlete cost at the moment?
  image_url: string,
  name: string,
  country_abbr: string,
  german_license: boolean,    // there is a restriction on num athletes without german license
  position: LineupPosition,   // position on field
  short_name?: string,        // for options with very long names
  team: TeamID,
  active: boolean,
  stat_prev_score?: number | null,
  stat_score_sum?: number | null,
  stat_perc_picked?: number | null,
};

export interface LineupSlotInfo {
  pick?: LineupOptionID,
  bought_for_budget?: number,     // how many did the user pay for this option? users will get back this amount when selling this athlete. When it's less than the athlete currently costs, the client will ask for confirmation
  score_info?: {                  // given if scoring started for this lineup
    part_of_scoring?: boolean,    // was this pick considered for the total_score of this lineup?
    score: number,
    score_details: LineupScoreDetail[],
  },
};

export interface LineupScoreDetail {
  key: string;
  amount: number;
  score: number;
}

export type LineupSlotInfoWithPickedOption = Omit<LineupSlotInfo, 'pick'> & { pickedOption: LineupOption } & { pick: LineupSlotInfo['pick'], slotKey: SlotKey };

export interface ILineup {
  id: LineupID,
  phase: LineupPhase,
  from_other?: boolean,       // is this lineup from another user?
  censored?: boolean,         // e.g. for other users lineups when matchday still open
  countdown_sec?: number,
  open_until: LineupDateTime, // When is countdown closed?
  total_budget: number,     // initial total budged available for this lineup
  budget_left: number,      // what's currently left? (should be total_budget - puck-cost for all picks when they were picked)
  score_info?: {            // only given when already scored
    total_score: number,
  },
  edit_info?: {             // given if open an user can edit (because it's their own lineup)
    can_edit: boolean,                  // should be true if this object is given, uneditable lineups don't need this info
    can_undo?: boolean,                 // if undo-action can be called (server just allows some steps and until some timestamps)
  },
  restrictions: {
    max_without_german_license?: number // should be 9 (?)
  },
  slots: Partial<{[key in SlotKey]: LineupSlotInfo}>, // picks for each slot
  validity: LineupValidity,
  round_id: number,
};

export type LineupEditAction =
  | LineupEditActionPick
  | LineupEditActionSwap
  | LineupEditActionClearSlot
  | LineupEditActionClearAll
  | LineupEditActionUndo

export interface LineupEditActionPick {
  type: 'pick';
  slot: SlotKey;
  option: LineupOptionID;
}
export interface LineupEditActionSwap {
  type: 'swap';
  slot1: SlotKey;
  slot2: SlotKey;
}
export interface LineupEditActionClearSlot {
  type: 'clear_slot';
  slot: SlotKey;
}
export interface LineupEditActionClearAll {
  type: 'clear_all';
}
export interface LineupEditActionUndo {
  type: 'undo';
}
